import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useLingui } from "@lingui/react/macro";

import { getBackend } from "@/lib/backend/backend2";
import type { IPlantData } from "@/lib/backend/manufacturing2.generated.types";
import type { IModelComparisonRow } from "@/lib/backend/models.generated.types";
import { waitForManufacturingAsync, getPlantsForUser } from "@/lib/businessLogic";
import {
    buildModelsComparisonReq,
    buildModelsStagingCsvRows,
    clampPageIndex,
    DEFAULT_MODELS_STAGING_FILTERS,
    fetchModelsComparisonForExport,
    isModelsStagingRowSelectable,
    mapApiRowToViewModel,
    type ModelsStagingFilterState,
    type ModelsStagingSortDirection,
    type ModelsStagingSortField,
} from "./modelsStaging.lib";
import { classNames, createCsvAndDownload } from "@/lib/util";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Loader from "@/modules/shared/ui/Loader";
import Pagination from "@/modules/shared/ui/Pagination";

import ModelsStagingFilters from "./ModelsStagingFilters";
import ModelsStagingExportModal from "./ModelsStagingExportModal";
import ModelsStagingTable from "./ModelsStagingTable";

import "./ModelsStaging.scss";

type PendingAction = "approve" | "reject" | null;

const ModelsStaging = () => {
    const { t } = useLingui();

    const [plants, setPlants] = useState<IPlantData[]>([]);
    const [ready, setReady] = useState(false);
    const [filters, setFilters] = useState<ModelsStagingFilterState>(DEFAULT_MODELS_STAGING_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<ModelsStagingFilterState>(DEFAULT_MODELS_STAGING_FILTERS);
    const [apiRows, setApiRows] = useState<IModelComparisonRow[]>([]);
    const [selectedModelCodes, setSelectedModelCodes] = useState<Set<string>>(() => new Set());
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [sortField, setSortField] = useState<ModelsStagingSortField>("old_vs_new_diff_pct");
    const [sortDirection, setSortDirection] = useState<ModelsStagingSortDirection>("desc");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalRows, setTotalRows] = useState(0);
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFilters, setExportFilters] = useState<ModelsStagingFilterState>(DEFAULT_MODELS_STAGING_FILTERS);
    const [exportAll, setExportAll] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState("");
    const actionInFlightRef = useRef(false);

    const rows = useMemo(
        () => apiRows.map(row => mapApiRowToViewModel(row, selectedModelCodes)),
        [apiRows, selectedModelCodes]
    );

    const selectableRows = useMemo(() => rows.filter(row => isModelsStagingRowSelectable(row)), [rows]);

    const selectedModelCodesList = useMemo(() => Array.from(selectedModelCodes), [selectedModelCodes]);

    const allSelected = selectableRows.length > 0 && selectableRows.every(row => row.is_selected);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            await waitForManufacturingAsync();
            const userPlants = getPlantsForUser();
            const defaultPlantUuid = userPlants.length > 0 ? userPlants[0].uuid : "";
            const nextFilters = {
                ...DEFAULT_MODELS_STAGING_FILTERS,
                plant_uuid: defaultPlantUuid,
            };

            if (cancelled) {
                return;
            }

            setPlants(userPlants);
            setFilters(nextFilters);
            setAppliedFilters(nextFilters);
            setReady(true);
        };

        init().catch(e => {
            if (!cancelled) {
                setError(e instanceof Error ? e.message : String(e));
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!ready) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError("");

        const load = async () => {
            try {
                const req = buildModelsComparisonReq(appliedFilters, sortField, sortDirection, pageIndex + 1, pageSize);
                const res = await getBackend().models.getModelsComparison(req);
                if (cancelled) {
                    return;
                }

                const clampedPageIndex = clampPageIndex(pageIndex, res.pagination.total_pages);
                if (clampedPageIndex !== null) {
                    setPageIndex(clampedPageIndex);
                    return;
                }

                setApiRows(res.rows);
                setTotalRows(res.pagination.total_rows);
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : String(e));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load().catch(e => {
            if (!cancelled) {
                setError(e instanceof Error ? e.message : String(e));
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [ready, appliedFilters, pageIndex, pageSize, sortField, sortDirection, reloadToken]);

    const handleSearch = () => {
        setPageIndex(0);
        setSelectedModelCodes(new Set());
        setAppliedFilters(filters);
    };

    const handleOpenExportModal = () => {
        setExportFilters(appliedFilters);
        setExportAll(false);
        setExportError("");
        setShowExportModal(true);
    };

    const handleCloseExportModal = () => {
        if (!exportLoading) {
            setShowExportModal(false);
            setExportError("");
        }
    };

    const handleExportCsv = async () => {
        setExportLoading(true);
        setExportError("");

        const filtersForExport = exportAll ? DEFAULT_MODELS_STAGING_FILTERS : exportFilters;

        try {
            const baseReq = buildModelsComparisonReq(filtersForExport, sortField, sortDirection, 1, 1);
            const allRows = await fetchModelsComparisonForExport(
                req => getBackend().models.getModelsComparison(req),
                baseReq
            );
            const csvRows = buildModelsStagingCsvRows(allRows, {
                plantExternalId: t`Plant External ID`,
                lineExternalId: t`Line External ID`,
                materialExternalId: t`Material External ID`,
                process: t`Process`,
                activeSlope: t`Active slope (Old)`,
                pendingSlope: t`Pending slope (New)`,
                normSlope: t`Norm slope`,
                oldVsNewDiffPct: t`Old vs New %`,
                oldVsNormDiffPct: t`Old vs Norm %`,
                newVsNormDiffPct: t`New vs Norm %`,
            });
            createCsvAndDownload(csvRows, `models_staging_${new Date().toISOString().slice(0, 10)}.csv`);
            setShowExportModal(false);
        } catch (e) {
            setExportError(e instanceof Error ? e.message : String(e));
        } finally {
            setExportLoading(false);
        }
    };

    const handleSort = (field: ModelsStagingSortField) => {
        setPageIndex(0);
        if (field === sortField) {
            setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortField(field);
        setSortDirection(field === "old_vs_new_diff_pct" ? "desc" : "asc");
    };

    const handleToggleRow = (modelCode: string) => {
        const row = apiRows.find(item => item.model_code === modelCode);
        if (row === undefined || !isModelsStagingRowSelectable(row)) {
            return;
        }

        setSelectedModelCodes(prev => {
            const next = new Set(prev);
            if (next.has(modelCode)) {
                next.delete(modelCode);
            } else {
                next.add(modelCode);
            }
            return next;
        });
    };

    const handleToggleAll = () => {
        const nextSelected = !allSelected;
        setSelectedModelCodes(prev => {
            const next = new Set(prev);
            for (const row of apiRows) {
                if (!isModelsStagingRowSelectable(row)) {
                    continue;
                }
                if (nextSelected) {
                    next.add(row.model_code);
                } else {
                    next.delete(row.model_code);
                }
            }
            return next;
        });
    };

    const handleConfirmAction = async () => {
        if (actionInFlightRef.current || actionLoading) {
            return;
        }
        if (pendingAction === null || selectedModelCodesList.length === 0) {
            setPendingAction(null);
            return;
        }

        actionInFlightRef.current = true;
        setActionLoading(true);
        setError("");
        setInfo("");

        try {
            if (pendingAction === "approve") {
                await getBackend().models.approvePendingModels({ model_codes: selectedModelCodesList });
                setInfo(t`Pending model versions approved.`);
            } else {
                await getBackend().models.rejectPendingModels({ model_codes: selectedModelCodesList });
                setInfo(t`Pending model versions rejected.`);
            }
            setPendingAction(null);
            setSelectedModelCodes(new Set());
            setPageIndex(0);
            setReloadToken(token => token + 1);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            actionInFlightRef.current = false;
            setActionLoading(false);
        }
    };

    const handleClosePendingActionModal = () => {
        if (!actionLoading) {
            setPendingAction(null);
        }
    };

    const pageCount = totalRows === 0 ? 0 : Math.ceil(totalRows / pageSize);

    return (
        <div className="w-100">
            <div className="row">
                <div className="col-md-12">
                    <div className="white_box space10i">
                        <h3>{t`Model staging`}</h3>

                        <ErrorComponent msg={error} type="error" />
                        <ErrorComponent msg={info} type="info" />

                        <ModelsStagingFilters
                            plants={plants}
                            filters={filters}
                            loading={loading}
                            onFiltersChange={setFilters}
                            onSearch={handleSearch}
                        />

                        <div className="models-staging-actions d-flex flex-wrap mb-3">
                            <Button
                                variant="primary"
                                disabled={selectedModelCodesList.length === 0 || actionLoading}
                                onClick={() => setPendingAction("approve")}
                            >
                                {t`Approve pending version(s)`}
                            </Button>
                            <Button
                                variant="outline-danger"
                                disabled={selectedModelCodesList.length === 0 || actionLoading}
                                onClick={() => setPendingAction("reject")}
                            >
                                {t`Reject pending version(s)`}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                className="btn_export"
                                disabled={!ready || loading || exportLoading}
                                onClick={handleOpenExportModal}
                            >
                                {t({ id: "common.export_csv", message: "Export CSV" })}
                            </Button>
                        </div>

                        {loading && rows.length === 0 ? (
                            <Loader />
                        ) : (
                            <>
                                <ModelsStagingTable
                                    rows={rows}
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    allSelected={allSelected}
                                    hasSelectableRows={selectableRows.length > 0}
                                    onSort={handleSort}
                                    onToggleAll={handleToggleAll}
                                    onToggleRow={handleToggleRow}
                                />
                                <Pagination
                                    num_rows={totalRows}
                                    page_count={pageCount}
                                    page_index={pageIndex}
                                    page_size={pageSize}
                                    page_options={Array.from({ length: pageCount }, (_, index) => index)}
                                    can_previous_page={pageIndex > 0}
                                    can_next_page={pageIndex + 1 < pageCount}
                                    gotoPage={setPageIndex}
                                    nextPage={() => setPageIndex(prev => prev + 1)}
                                    previousPage={() => setPageIndex(prev => Math.max(0, prev - 1))}
                                    setPageSize={size => {
                                        setPageIndex(0);
                                        setPageSize(size);
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Modal show={pendingAction !== null} animation={false} onHide={handleClosePendingActionModal}>
                <Modal.Header>
                    <Modal.Title>
                        {pendingAction === "approve" ? t`Approve pending versions` : t`Reject pending versions`}
                    </Modal.Title>
                    <button
                        type="button"
                        className="close"
                        disabled={actionLoading}
                        onClick={handleClosePendingActionModal}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    {pendingAction === "approve"
                        ? t`Approve ${selectedModelCodesList.length} pending model version(s)? Active versions will be replaced.`
                        : t`Reject ${selectedModelCodesList.length} pending model version(s)? Pending data will be deleted.`}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={actionLoading}
                        onClick={handleClosePendingActionModal}
                    >
                        {t`Cancel`}
                    </button>
                    <button
                        type="button"
                        className={classNames("btn", {
                            "btn-danger": pendingAction === "reject",
                            "btn-primary": pendingAction === "approve",
                        })}
                        disabled={actionLoading}
                        onClick={() => {
                            void handleConfirmAction();
                        }}
                    >
                        {actionLoading ? t`Processing…` : pendingAction === "approve" ? t`Approve` : t`Reject`}
                    </button>
                </Modal.Footer>
            </Modal>

            <ModelsStagingExportModal
                show={showExportModal}
                plants={plants}
                exportFilters={exportFilters}
                exportAll={exportAll}
                loading={exportLoading}
                error={exportError}
                onExportFiltersChange={setExportFilters}
                onExportAllChange={setExportAll}
                onUseCurrentFilters={() => setExportFilters(appliedFilters)}
                onExport={() => {
                    void handleExportCsv();
                }}
                onClose={handleCloseExportModal}
            />
        </div>
    );
};

export default ModelsStaging;
